// Web API client — mirrors mobile src/api.ts against the shared FastAPI backend.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "hsc-token";
const ROLE_KEY = "hsc-role";
const MEMBER_KEY = "hsc-member-id";
const NAME_KEY = "hsc-name";

export function saveSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, user.role || "");
  localStorage.setItem(MEMBER_KEY, user.member_id || "");
  localStorage.setItem(NAME_KEY, user.name || "");
  localStorage.setItem("hsc-lines", JSON.stringify(user.lines || []));
  localStorage.setItem("hsc-all-access", user.all_access ? "1" : "");
  localStorage.setItem("hsc-user", JSON.stringify(user));
}

export function getSession() {
  let user = null;
  try { user = JSON.parse(localStorage.getItem("hsc-user") || "null"); } catch {}
  return {
    token: localStorage.getItem(TOKEN_KEY) || "",
    role: localStorage.getItem(ROLE_KEY) || null,
    memberId: localStorage.getItem(MEMBER_KEY) || "",
    name: localStorage.getItem(NAME_KEY) || "",
    user,
  };
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function clearSession() {
  [TOKEN_KEY, ROLE_KEY, MEMBER_KEY, NAME_KEY, "hsc-lines", "hsc-all-access", "hsc-user"].forEach((k) =>
    localStorage.removeItem(k)
  );
}

async function req(path, opts = {}, auth = false) {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    let detail = `${res.status}`;
    try { const j = await res.json(); detail = j.detail || detail; } catch {}
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  return res.json();
}

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${BACKEND_URL}${pathOrUrl}`;
  return `${API}/files/${pathOrUrl}`;
}

export async function uploadImage(file) {
  const token = getToken();
  const form = new FormData();
  form.append("file", file, file.name || "image.jpg");
  const res = await fetch(`${API}/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!res.ok) { let d = `${res.status}`; try { d = (await res.json()).detail || d; } catch {} throw new Error(d); }
  return res.json();
}

export async function fetchTransactionsCsv(params) {
  const token = getToken();
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.append(k, String(v)); });
  const res = await fetch(`${API}/owner/analytics/transactions.csv?${qs.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.text();
}

export const api = {
  // Auth
  register: (p) => req(`/auth/register`, { method: "POST", body: JSON.stringify(p) }),
  login: (identifier, password) => req(`/auth/login`, { method: "POST", body: JSON.stringify({ identifier, password }) }),
  me: () => req(`/me`, {}, true),
  changePassword: (current_password, new_password) => req(`/me/password`, { method: "POST", body: JSON.stringify({ current_password, new_password }) }, true),
  resetMemberPassword: (memberId, new_password) => req(`/owner/members/${memberId}/password`, { method: "POST", body: JSON.stringify({ new_password }) }, true),
  authSession: (session_id) => req(`/auth/session`, { method: "POST", body: JSON.stringify({ session_id }) }),

  // Member
  getMember: (id) => req(`/member/${id}`),
  resolveMemberQr: (code) => req(`/members/resolve-qr?code=${encodeURIComponent(code)}`, {}, true),
  getSpendCategories: (id) => req(`/member/${id}/spend/categories`),
  getSpendMonthly: (id) => req(`/member/${id}/spend/monthly`),
  getAchievements: (id) => req(`/member/${id}/achievements`),
  getTransactions: (id, category) => req(`/member/${id}/transactions${category && category !== "All" ? `?category=${category}` : ""}`),
  addTransaction: (id, body) => req(`/member/${id}/transactions`, { method: "POST", body: JSON.stringify(body) }, true),

  // Rewards
  getRewards: () => req(`/rewards`),
  createReward: (body) => req(`/rewards`, { method: "POST", body: JSON.stringify(body) }, true),
  deleteReward: (id) => req(`/rewards/${id}`, { method: "DELETE" }, true),
  rewardRedemptions: (id) => req(`/rewards/${id}/redemptions`, {}, true),
  getRedemptions: (id) => req(`/member/${id}/redemptions`),
  redeem: (rewardId, memberId) => req(`/rewards/redeem`, { method: "POST", body: JSON.stringify({ reward_id: rewardId, member_id: memberId }) }),

  // Memberships
  memberMemberships: (id) => req(`/member/${id}/memberships`),
  ownerMemberships: () => req(`/owner/memberships`, {}, true),

  // Bookings
  getSlots: (date, sport) => req(`/bookings/slots?date=${date}${sport && sport !== "All" ? `&sport=${sport}` : ""}`),
  getMyBookings: (id) => req(`/member/${id}/bookings`),

  // Owner members / team
  ownerMembers: (q) => req(`/owner/members${q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`, {}, true),
  ownerMembersStats: () => req(`/owner/members/stats`, {}, true),
  ownerCreateMember: (body) => req(`/owner/create-member`, { method: "POST", body: JSON.stringify(body) }, true),
  updateMember: (id, body) => req(`/member/${id}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  ownerCreateAdmin: (email, password, name, opts) => req(`/owner/create-admin`, { method: "POST", body: JSON.stringify({ email, password, name, lines: opts.lines, all_access: opts.all_access }) }, true),
  ownerAdmins: () => req(`/owner/admins`, {}, true),
  createCoach: (body) => req(`/owner/coaches`, { method: "POST", body: JSON.stringify(body) }, true),
  listCoaches: () => req(`/owner/coaches`, {}, true),
  updateCoach: (id, body) => req(`/owner/coaches/${id}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  deleteCoach: (id) => req(`/owner/coaches/${id}`, { method: "DELETE" }, true),
  facilities: () => req(`/facilities`),

  // Transactions / ledger
  adminLineTransactions: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.q) qs.set("q", params.q);
    if (params.category && params.category !== "all") qs.set("category", params.category);
    if (params.txn_type && params.txn_type !== "all") qs.set("txn_type", params.txn_type);
    const s = qs.toString();
    return req(`/admin/line-transactions${s ? `?${s}` : ""}`, {}, true);
  },
  updateTransaction: (txnId, body) => req(`/transactions/${txnId}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  deleteTransaction: (txnId) => req(`/transactions/${txnId}`, { method: "DELETE" }, true),
  listCategories: () => req(`/categories`, {}, true),
  getBill: (billId) => req(`/admin/bill/${billId}`, {}, true),
  addPayment: (txnId, body) => req(`/transactions/${txnId}/payment`, { method: "POST", body: JSON.stringify(body) }, true),

  // Points
  pointsHistory: (memberId) => req(`/member/${memberId}/points-history`, {}, true),
  addPointsAdjustment: (memberId, body) => req(`/owner/members/${memberId}/points-adjustment`, { method: "POST", body: JSON.stringify(body) }, true),

  // Data health
  ownerDataHealth: () => req(`/owner/data-health`, {}, true),
  ownerOutstanding: () => req(`/owner/outstanding`, {}, true),

  // Check-ins
  createCheckin: (body) => req(`/checkins`, { method: "POST", body: JSON.stringify(body) }, true),
  checkinOptions: (memberId) => req(`/checkin-options/${memberId}`, {}, true),
  listCheckins: (date) => req(`/checkins${date ? `?date=${date}` : ""}`, {}, true),

  // Classes
  classesSummary: () => req(`/classes/summary`),
  classesAvailable: (memberId, date) => req(`/classes/available?${new URLSearchParams({ ...(memberId ? { member_id: memberId } : {}), ...(date ? { date } : {}) }).toString()}`),
  enrollClass: (body) => req(`/classes/enroll`, { method: "POST", body: JSON.stringify(body) }),
  unenrollClass: (body) => req(`/classes/unenroll`, { method: "POST", body: JSON.stringify(body) }),
  memberClasses: (id) => req(`/member/${id}/classes`),
  classSchedule: () => req(`/classes/schedule`, {}, true),
  addClassSchedule: (body, force = false) => req(`/classes/schedule${force ? "?force=true" : ""}`, { method: "POST", body: JSON.stringify(body) }, true),
  updateClassSchedule: (id, body, force = false) => req(`/classes/schedule/${id}${force ? "?force=true" : ""}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  deleteClassSchedule: (id) => req(`/classes/schedule/${id}`, { method: "DELETE" }, true),
  classRoster: (className, date, time) => req(`/classes/roster?class_name=${encodeURIComponent(className)}&date=${encodeURIComponent(date)}&time=${encodeURIComponent(time)}`, {}, true),
  classCategories: () => req(`/class-categories`),
  classPendingPayments: () => req(`/classes/pending-payments`, {}, true),
  classMarkPaid: (body) => req(`/classes/mark-paid`, { method: "POST", body: JSON.stringify(body) }, true),
  classConfirmMembership: (body) => req(`/classes/confirm-membership`, { method: "POST", body: JSON.stringify(body) }, true),

  // Coach
  coachClasses: () => req(`/coach/classes`, {}, true),
  coachRoster: (class_name, date, time) => req(`/coach/roster?class_name=${encodeURIComponent(class_name)}${date ? `&date=${date}` : ""}${time ? `&time=${encodeURIComponent(time)}` : ""}`, {}, true),
  coachCheckinHistory: () => req(`/coach/checkin-history`, {}, true),
  coachCheckin: (class_name, date, time, schedule_id) => req(`/coach/checkin`, { method: "POST", body: JSON.stringify({ class_name, date, time, schedule_id }) }, true),

  // Analytics
  ownerAnalytics: (from, to) => req(`/owner/analytics?from=${from}&to=${to}`, {}, true),
  ownerAnalyticsTransactions: (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.append(k, String(v)); });
    return req(`/owner/analytics/transactions?${qs.toString()}`, {}, true);
  },

  // Promos
  listPromos: () => req(`/promos`),
  ownerPromos: () => req(`/owner/promos`, {}, true),
  createPromo: (body) => req(`/owner/promos`, { method: "POST", body: JSON.stringify(body) }, true),
  deletePromo: (id) => req(`/owner/promos/${id}`, { method: "DELETE" }, true),

  // Notifications
  listNotifications: () => req(`/notifications`, {}, true),
  unreadCount: () => req(`/notifications/unread-count`, {}, true),
  readAllNotifications: () => req(`/notifications/read-all`, { method: "POST" }, true),
  readNotification: (id) => req(`/notifications/${id}/read`, { method: "POST" }, true),
  getNotifSettings: () => req(`/owner/notification-settings`, {}, true),
  updateNotifSettings: (body) => req(`/owner/notification-settings`, { method: "PUT", body: JSON.stringify(body) }, true),
};
